import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, Search } from 'lucide-react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { DatePicker } from '../ui/date-picker';
import { FileText, Download, Plus, Trash2, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleString('bg-BG', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit'
  });
};

const formatDuration = (d) => {
  const m = Math.floor(d);
  const s = Math.round((d - m) * 60);
  return s > 0 ? `${m} мин ${s} сек` : `${m} мин`;
};

const getFirstDayOfMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
};

const getMonthName = () => {
  return new Date().toLocaleString('bg-BG', { month: 'long', year: 'numeric' });
};

export const EntriesTab = ({ employees, parts }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    employee_id: 'all', part_id: 'all', operation_number: 'all', client: 'all',
    start_date: getFirstDayOfMonth(), end_date: ''
  });
  const [showManualEntryDialog, setShowManualEntryDialog] = useState(false);
  const [manualEntryForm, setManualEntryForm] = useState({
    employee_id: '', part_id: '', operation_number: '',
    quantity: '', defects: '0', comments: '', entry_date: ''
  });
  const [manualEntryPart, setManualEntryPart] = useState(null);
  const [manualPartSearch, setManualPartSearch] = useState('');
  const [showManualPartDrop, setShowManualPartDrop] = useState(false);
  const manualPartRef = useRef(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editForm, setEditForm] = useState({ employee_id: '', part_id: '', operation_number: '', quantity: '', defects: '0', comments: '', entry_date: '' });
  const [editPart, setEditPart] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'entry_date', direction: 'desc' });

  const handleSort = useCallback((key) => {
    setSortConfig(prev =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === 'entry_date' ? 'desc' : 'asc' }
    );
  }, []);

  const sortedEntries = useMemo(() => {
    if (!sortConfig.key) return entries;
    return [...entries].sort((a, b) => {
      let aVal, bVal;
      switch (sortConfig.key) {
        case 'entry_date':   aVal = a.entry_date || ''; bVal = b.entry_date || ''; break;
        case 'employee':     aVal = a.employee?.full_name || ''; bVal = b.employee?.full_name || ''; break;
        case 'part_name':    aVal = a.part?.name || ''; bVal = b.part?.name || ''; break;
        case 'part_code':    aVal = a.part?.code || ''; bVal = b.part?.code || ''; break;
        case 'client':       aVal = a.part?.client_name || ''; bVal = b.part?.client_name || ''; break;
        case 'op_number':    aVal = a.operation_number || 0; bVal = b.operation_number || 0; break;
        case 'op_name':      aVal = a.operation?.operation_name || ''; bVal = b.operation?.operation_name || ''; break;
        case 'duration':     aVal = a.operation?.duration_minutes || 0; bVal = b.operation?.duration_minutes || 0; break;
        case 'quantity':     aVal = a.quantity || 0; bVal = b.quantity || 0; break;
        case 'defects':      aVal = a.defects || 0; bVal = b.defects || 0; break;
        case 'total_mins':   aVal = (a.quantity || 0) * (a.operation?.duration_minutes || 0);
                             bVal = (b.quantity || 0) * (b.operation?.duration_minutes || 0); break;
        default: return 0;
      }
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [entries, sortConfig]);

  // Зарежда записите при стартиране (текущ месец)
  useEffect(() => {
    loadEntries();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEntries = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach((key) => {
        if (filters[key] && filters[key] !== 'all') params.append(key, filters[key]);
      });
      const response = await axios.get(`${API}/production-entries?${params.toString()}`);
      setEntries(response.data);
    } catch {
      toast.error('Грешка при зареждане на данни');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = useCallback((entry) => {
    const entryDateOnly = entry.entry_date ? entry.entry_date.split('T')[0] : '';
    setEditForm({
      employee_id: entry.employee_id || '',
      part_id: entry.part_id || '',
      operation_number: entry.operation_number?.toString() || '',
      quantity: entry.quantity?.toString() || '',
      defects: entry.defects?.toString() || '0',
      comments: entry.comments || '',
      entry_date: entryDateOnly,
    });
    const selectedPart = parts.find(p => p.id === entry.part_id) || null;
    setEditPart(selectedPart);
    setEditingEntry(entry);
  }, [parts]);

  const handleEditSubmit = useCallback(async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/production-entries/admin-update/${editingEntry.id}`, {
        employee_id: editForm.employee_id || undefined,
        part_id: editForm.part_id || undefined,
        operation_number: editForm.operation_number ? parseInt(editForm.operation_number) : undefined,
        quantity: editForm.quantity ? parseInt(editForm.quantity) : undefined,
        defects: editForm.defects !== '' ? parseInt(editForm.defects) : undefined,
        comments: editForm.comments || undefined,
        entry_date: editForm.entry_date || undefined,
      });
      toast.success('Записът е актуализиран');
      setEditingEntry(null);
      loadEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Грешка при редактиране');
    }
  }, [editingEntry, editForm, loadEntries]);

  const handleExportPDF = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.employee_id !== 'all') params.append('employee_id', filters.employee_id);
      if (filters.part_id !== 'all') params.append('part_id', filters.part_id);
      if (filters.operation_number !== 'all') params.append('operation_number', filters.operation_number);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      const response = await axios.get(`${API}/export/pdf?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'production_report.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF файлът е изтеглен успешно');
    } catch {
      toast.error('Грешка при изтегляне на PDF');
    }
  };

  const handleExportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.employee_id !== 'all') params.append('employee_id', filters.employee_id);
      if (filters.part_id !== 'all') params.append('part_id', filters.part_id);
      if (filters.operation_number !== 'all') params.append('operation_number', filters.operation_number);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      const response = await axios.get(`${API}/export/excel?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'production_data.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel файлът е изтеглен успешно');
    } catch {
      toast.error('Грешка при изтегляне на данни');
    }
  };

  const handleManualEntrySubmit = async (e) => {
    e.preventDefault();
    if (!manualEntryForm.employee_id || !manualEntryForm.part_id || !manualEntryForm.operation_number || !manualEntryForm.quantity || !manualEntryForm.entry_date) {
      toast.error('Моля попълнете всички задължителни полета');
      return;
    }
    try {
      await axios.post(`${API}/production-entries/admin-create`, {
        employee_id: manualEntryForm.employee_id,
        part_id: manualEntryForm.part_id,
        operation_number: parseInt(manualEntryForm.operation_number),
        quantity: parseInt(manualEntryForm.quantity),
        defects: parseInt(manualEntryForm.defects) || 0,
        comments: manualEntryForm.comments || null,
        entry_date: manualEntryForm.entry_date
      });
      toast.success('Записът е добавен успешно');
      setShowManualEntryDialog(false);
      setManualEntryForm({ employee_id: '', part_id: '', operation_number: '', quantity: '', defects: '0', comments: '', entry_date: '' });
      setManualEntryPart(null);
      setManualPartSearch('');
      loadEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Грешка при добавяне на запис');
    }
  };

  const handleAdminDelete = async (entryId) => {
    if (!window.confirm('Сигурни ли сте, че искате да изтриете този запис? Тази операция е необратима.')) return;
    try {
      await axios.delete(`${API}/production-entries/admin/${entryId}`);
      toast.success('Записът е изтрит');
      loadEntries();
    } catch {
      toast.error('Грешка при изтриване');
    }
  };

  const getAllOperationNumbers = () => {
    const numbers = new Set();
    parts.forEach(p => p.operations?.forEach(op => numbers.add(op.operation_number)));
    return Array.from(numbers).sort((a, b) => a - b);
  };

  const getAllClients = () => {
    const clients = new Set();
    parts.forEach(p => { if (p.client_name) clients.add(p.client_name); });
    return Array.from(clients).sort();
  };

  const visibleEntries = useMemo(() => {
    if (filters.client === 'all') return sortedEntries;
    return sortedEntries.filter(e => e.part?.client_name === filters.client);
  }, [sortedEntries, filters.client]);

  const totalQuantity = entries.reduce((sum, e) => sum + (e.quantity || 0), 0);
  const totalDefects = entries.reduce((sum, e) => sum + (e.defects || 0), 0);
  const totalMinutes = entries.reduce((sum, e) => sum + ((e.quantity || 0) * (e.operation?.duration_minutes || 0)), 0);

  return (
    <>
      {/* Filters */}
      <Card className="p-4 md:p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="heading-font text-xl font-bold uppercase">Филтри</h2>
          <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded font-medium">
            {filters.start_date ? `от ${filters.start_date}` : 'Всички периоди'}
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wide mb-1 block">Служител</Label>
            <Select value={filters.employee_id} onValueChange={(v) => setFilters({ ...filters, employee_id: v })}>
              <SelectTrigger data-testid="filter-employee"><SelectValue placeholder="Всички" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Всички</SelectItem>
                {employees.map(emp => <SelectItem key={emp.id} value={emp.id}>{emp.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide mb-1 block">Детайл</Label>
            <Select value={filters.part_id} onValueChange={(v) => setFilters({ ...filters, part_id: v })}>
              <SelectTrigger data-testid="filter-part"><SelectValue placeholder="Всички" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Всички</SelectItem>
                {parts.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.code})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide mb-1 block">Номер на операция</Label>
            <Select value={filters.operation_number} onValueChange={(v) => setFilters({ ...filters, operation_number: v })}>
              <SelectTrigger data-testid="filter-operation"><SelectValue placeholder="Всички" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Всички</SelectItem>
                {getAllOperationNumbers().map(num => <SelectItem key={num} value={num.toString()}>Операция {num}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide mb-1 block">Клиент</Label>
            <Select value={filters.client} onValueChange={(v) => setFilters({ ...filters, client: v })}>
              <SelectTrigger data-testid="filter-client"><SelectValue placeholder="Всички" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Всички</SelectItem>
                {getAllClients().map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide mb-1 block">От дата</Label>
            <DatePicker value={filters.start_date} onChange={(v) => setFilters({ ...filters, start_date: v })} placeholder="Изберете начална дата" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide mb-1 block">До дата</Label>
            <DatePicker value={filters.end_date} onChange={(v) => setFilters({ ...filters, end_date: v })} placeholder="Изберете крайна дата" />
          </div>
        </div>
        <div className="flex gap-3 mt-4">
          <Button data-testid="apply-filters-button" onClick={loadEntries} className="bg-red-600 hover:bg-red-700 font-bold uppercase">Приложи филтри</Button>
          <Button data-testid="reset-filters-button" onClick={() => setFilters({ employee_id: 'all', part_id: 'all', operation_number: 'all', client: 'all', start_date: getFirstDayOfMonth(), end_date: '' })} variant="outline" className="border-2 font-semibold uppercase">Текущ месец</Button>
          <Button data-testid="all-time-button" onClick={() => setFilters({ employee_id: 'all', part_id: 'all', operation_number: 'all', client: 'all', start_date: '', end_date: '' })} variant="outline" className="border-2 font-semibold uppercase text-slate-500">Архив (всички)</Button>
        </div>
      </Card>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4"><p className="text-xs text-slate-600 uppercase">Записи</p><p className="mono-font text-2xl font-bold">{entries.length}</p></Card>
        <Card className="p-4"><p className="text-xs text-slate-600 uppercase">Бройки</p><p className="mono-font text-2xl font-bold text-green-600">{totalQuantity}</p></Card>
        <Card className="p-4"><p className="text-xs text-slate-600 uppercase">Брак</p><p className="mono-font text-2xl font-bold text-red-600">{totalDefects}</p></Card>
        <Card className="p-4"><p className="text-xs text-slate-600 uppercase">Минути</p><p className="mono-font text-2xl font-bold text-blue-600">{Math.round(totalMinutes * 100) / 100}</p></Card>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Button data-testid="export-pdf-button" onClick={handleExportPDF} className="bg-red-600 hover:bg-red-700 font-bold uppercase text-sm">
          <FileText className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Изтегли PDF</span><span className="sm:hidden">PDF</span>
        </Button>
        <Button data-testid="export-excel-button" onClick={handleExportExcel} className="bg-green-600 hover:bg-green-700 font-bold uppercase text-sm">
          <Download className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Изтегли Excel</span><span className="sm:hidden">Excel</span>
        </Button>
        <Button data-testid="add-manual-entry-button" onClick={() => { setManualEntryForm({ employee_id: '', part_id: '', operation_number: '', quantity: '', defects: '0', comments: '', entry_date: '' }); setManualEntryPart(null); setManualPartSearch(''); setShowManualEntryDialog(true); }} variant="outline" className="border-2 border-slate-700 font-bold uppercase text-sm ml-auto">
          <Plus className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Добави запис</span><span className="sm:hidden">+ Запис</span>
        </Button>
      </div>

      {/* Table */}
      <Card className="p-4 sm:p-6">
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <Table>
            <TableHeader>
              <TableRow>
                {[
                  { label: 'Дата',      key: 'entry_date'  },
                  { label: 'Служител',  key: 'employee'    },
                  { label: 'Детайл',    key: 'part_name'   },
                  { label: 'Код',       key: 'part_code'   },
                  { label: 'Клиент',    key: 'client'      },
                  { label: 'Оп. №',     key: 'op_number'   },
                  { label: 'Операция',  key: 'op_name'     },
                  { label: 'Мин/бр',    key: 'duration'    },
                  { label: 'Бройки',    key: 'quantity'    },
                  { label: 'Брак',      key: 'defects'     },
                  { label: 'Минути',    key: 'total_mins'  },
                  { label: 'Коментари', key: null          },
                  { label: '',          key: null          },
                ].map(({ label, key }) => (
                  <TableHead
                    key={label}
                    className={`uppercase text-xs whitespace-nowrap ${key ? 'cursor-pointer select-none hover:bg-slate-100 transition-colors' : ''}`}
                    onClick={key ? () => handleSort(key) : undefined}
                  >
                    {key ? (
                      <span className="inline-flex items-center gap-0.5">
                        {label}
                        {sortConfig.key === key ? (
                          sortConfig.direction === 'asc'
                            ? <ChevronUp className="w-3 h-3 text-red-600 shrink-0" />
                            : <ChevronDown className="w-3 h-3 text-red-600 shrink-0" />
                        ) : (
                          <ChevronsUpDown className="w-3 h-3 text-slate-300 shrink-0" />
                        )}
                      </span>
                    ) : label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={13} className="text-center py-8">Зареждане...</TableCell></TableRow>
              ) : visibleEntries.length === 0 ? (
                <TableRow><TableCell colSpan={13} className="text-center py-8 text-slate-600">Няма намерени записи</TableCell></TableRow>
              ) : visibleEntries.map((entry) => {
                const duration = entry.operation?.duration_minutes || 0;
                const totalMins = (entry.quantity || 0) * duration;
                return (
                  <TableRow key={entry.id}>
                    <TableCell className="mono-font text-sm">{formatDate(entry.entry_date)}</TableCell>
                    <TableCell>{entry.employee?.full_name || '-'}</TableCell>
                    <TableCell>{entry.part?.name || '-'}</TableCell>
                    <TableCell className="mono-font">{entry.part?.code || '-'}</TableCell>
                    <TableCell className="text-slate-600">{entry.part?.client_name || '-'}</TableCell>
                    <TableCell className="mono-font font-bold text-center">{entry.operation_number || '-'}</TableCell>
                    <TableCell>{entry.operation?.operation_name || '-'}</TableCell>
                    <TableCell className="mono-font text-center">{formatDuration(duration)}</TableCell>
                    <TableCell className="mono-font font-bold">{entry.quantity}</TableCell>
                    <TableCell className="mono-font text-red-600">{entry.defects || 0}</TableCell>
                    <TableCell className="mono-font font-bold text-blue-600">{Math.round(totalMins * 100) / 100}</TableCell>
                    <TableCell className="max-w-xs truncate">{entry.comments || '-'}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          data-testid={`admin-edit-entry-${entry.id}`}
                          onClick={() => openEditDialog(entry)}
                          variant="ghost"
                          size="sm"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-8 w-8 p-0"
                          title="Редактирай запис"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`admin-delete-entry-${entry.id}`}
                          onClick={() => handleAdminDelete(entry.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                          title="Изтрий запис"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            </Table>
          </div>
        </div>
      </Card>

      {/* Manual Entry Dialog */}
      <Dialog open={showManualEntryDialog} onOpenChange={setShowManualEntryDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="heading-font text-xl font-bold uppercase">Добави запис</DialogTitle></DialogHeader>
          <form onSubmit={handleManualEntrySubmit} className="space-y-4 mt-2">
            <div>
              <Label className="text-sm uppercase tracking-wide mb-2 block">Служител *</Label>
              <Select value={manualEntryForm.employee_id} onValueChange={(v) => setManualEntryForm({ ...manualEntryForm, employee_id: v })}>
                <SelectTrigger data-testid="manual-entry-employee" className="border-2"><SelectValue placeholder="Изберете служител" /></SelectTrigger>
                <SelectContent>{employees.map(emp => <SelectItem key={emp.id} value={emp.id}>{emp.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm uppercase tracking-wide mb-2 block">Детайл *</Label>
              <div ref={manualPartRef} className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
                <Input
                  data-testid="manual-entry-part-search"
                  value={manualPartSearch}
                  onChange={(e) => {
                    setManualPartSearch(e.target.value);
                    setShowManualPartDrop(true);
                    if (manualEntryPart) { setManualEntryPart(null); setManualEntryForm(f => ({ ...f, part_id: '', operation_number: '' })); }
                  }}
                  onFocus={() => setShowManualPartDrop(true)}
                  onBlur={() => setTimeout(() => setShowManualPartDrop(false), 200)}
                  className="border-2 pl-9"
                  placeholder="Търси по име или код..."
                  autoComplete="off"
                />
                {showManualPartDrop && (
                  <div className="absolute z-50 w-full mt-1 bg-white border-2 border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {parts.filter(p => {
                      const q = manualPartSearch.toLowerCase();
                      return !q || p.name?.toLowerCase().includes(q) || p.code?.toLowerCase().includes(q);
                    }).length === 0 ? (
                      <div className="p-3 text-center text-slate-500 text-sm">Няма намерени детайли</div>
                    ) : parts.filter(p => {
                      const q = manualPartSearch.toLowerCase();
                      return !q || p.name?.toLowerCase().includes(q) || p.code?.toLowerCase().includes(q);
                    }).map(p => (
                      <div
                        key={p.id}
                        onMouseDown={() => {
                          setManualEntryPart(p);
                          setManualPartSearch(`${p.name} — ${p.code}`);
                          setManualEntryForm(f => ({ ...f, part_id: p.id, operation_number: '' }));
                          setShowManualPartDrop(false);
                        }}
                        className={`p-3 cursor-pointer hover:bg-red-50 border-b last:border-b-0 ${manualEntryPart?.id === p.id ? 'bg-red-100' : ''}`}
                      >
                        <div className="font-medium text-sm">{p.name}</div>
                        <div className="text-xs text-slate-500">{p.code}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label className="text-sm uppercase tracking-wide mb-2 block">Операция *</Label>
              <Select value={manualEntryForm.operation_number} onValueChange={(v) => setManualEntryForm({ ...manualEntryForm, operation_number: v })} disabled={!manualEntryPart}>
                <SelectTrigger data-testid="manual-entry-operation" className="border-2"><SelectValue placeholder={manualEntryPart ? "Изберете операция" : "Първо изберете детайл"} /></SelectTrigger>
                <SelectContent>{(manualEntryPart?.operations || []).map(op => <SelectItem key={op.operation_number} value={op.operation_number.toString()}>{op.operation_number}. {op.operation_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm uppercase tracking-wide mb-2 block">Дата *</Label>
              <Input data-testid="manual-entry-date" type="date" max={new Date().toISOString().split('T')[0]} value={manualEntryForm.entry_date} onChange={(e) => setManualEntryForm({ ...manualEntryForm, entry_date: e.target.value })} className="border-2" required />
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <Label className="text-sm uppercase tracking-wide mb-2 block">Бройки *</Label>
                <Input data-testid="manual-entry-quantity" type="number" min="1" value={manualEntryForm.quantity} onChange={(e) => setManualEntryForm({ ...manualEntryForm, quantity: e.target.value })} className="border-2" placeholder="0" required />
              </div>
              <div className="flex-1">
                <Label className="text-sm uppercase tracking-wide mb-2 block">Брак</Label>
                <Input data-testid="manual-entry-defects" type="number" min="0" value={manualEntryForm.defects} onChange={(e) => setManualEntryForm({ ...manualEntryForm, defects: e.target.value })} className="border-2" placeholder="0" />
              </div>
            </div>
            <div>
              <Label className="text-sm uppercase tracking-wide mb-2 block">Коментар</Label>
              <Input data-testid="manual-entry-comments" type="text" value={manualEntryForm.comments} onChange={(e) => setManualEntryForm({ ...manualEntryForm, comments: e.target.value })} className="border-2" placeholder="Незадължително" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button data-testid="manual-entry-submit" type="submit" className="flex-1 bg-red-600 hover:bg-red-700 font-bold uppercase">Добави запис</Button>
              <Button type="button" onClick={() => setShowManualEntryDialog(false)} variant="outline" className="flex-1 border-2 font-semibold uppercase">Отказ</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      {/* Edit Entry Dialog */}
      <Dialog open={!!editingEntry} onOpenChange={(open) => !open && setEditingEntry(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="heading-font text-xl font-bold uppercase">Редактиране на запис</DialogTitle></DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4 mt-2">
            <div>
              <Label className="text-sm uppercase tracking-wide mb-2 block">Служител *</Label>
              <Select value={editForm.employee_id} onValueChange={(v) => setEditForm({ ...editForm, employee_id: v })}>
                <SelectTrigger className="border-2"><SelectValue placeholder="Изберете служител" /></SelectTrigger>
                <SelectContent>{employees.map(emp => <SelectItem key={emp.id} value={emp.id}>{emp.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm uppercase tracking-wide mb-2 block">Детайл *</Label>
              <Select value={editForm.part_id} onValueChange={(v) => { const sel = parts.find(p => p.id === v); setEditPart(sel || null); setEditForm({ ...editForm, part_id: v, operation_number: '' }); }}>
                <SelectTrigger className="border-2"><SelectValue placeholder="Изберете детайл" /></SelectTrigger>
                <SelectContent>{parts.map(p => <SelectItem key={p.id} value={p.id}>{p.name} {p.code ? `(${p.code})` : ''}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm uppercase tracking-wide mb-2 block">Операция *</Label>
              <Select value={editForm.operation_number} onValueChange={(v) => setEditForm({ ...editForm, operation_number: v })} disabled={!editPart}>
                <SelectTrigger className="border-2"><SelectValue placeholder={editPart ? "Изберете операция" : "Първо изберете детайл"} /></SelectTrigger>
                <SelectContent>{(editPart?.operations || []).map(op => <SelectItem key={op.operation_number} value={op.operation_number.toString()}>{op.operation_number}. {op.operation_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm uppercase tracking-wide mb-2 block">Дата *</Label>
              <Input type="date" max={new Date().toISOString().split('T')[0]} value={editForm.entry_date} onChange={(e) => setEditForm({ ...editForm, entry_date: e.target.value })} className="border-2" required />
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <Label className="text-sm uppercase tracking-wide mb-2 block">Бройки *</Label>
                <Input type="number" min="1" value={editForm.quantity} onChange={(e) => setEditForm({ ...editForm, quantity: e.target.value })} className="border-2" required />
              </div>
              <div className="flex-1">
                <Label className="text-sm uppercase tracking-wide mb-2 block">Брак</Label>
                <Input type="number" min="0" value={editForm.defects} onChange={(e) => setEditForm({ ...editForm, defects: e.target.value })} className="border-2" />
              </div>
            </div>
            <div>
              <Label className="text-sm uppercase tracking-wide mb-2 block">Коментар</Label>
              <Input type="text" value={editForm.comments} onChange={(e) => setEditForm({ ...editForm, comments: e.target.value })} className="border-2" placeholder="Незадължително" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 font-bold uppercase">Запази промените</Button>
              <Button type="button" onClick={() => setEditingEntry(null)} variant="outline" className="flex-1 border-2 font-semibold uppercase">Отказ</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
